import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { useI18n, useLogger, useSelector } from '~Hooks';
import IconButton from '~ComponentsImproved/IconButton';
import SystemIcon from '~Assets/icons/sync.svg';
import Icon from '~ComponentsImproved/Icon';
import useModal from '~ComponentsImproved/Modal/useModal';
import SlideInPanel from '~ComponentsImproved/SlideInPanel';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import CheckCircleIcon from '~Assets/icons/check-circle.svg';
import WarningIcon from '~Assets/icons/warning.svg';
import ErrorRow from './ErrorRow';
import InternetStatus from '~Components/InternetStatus';
import _ from 'lodash';
import HighEventVolume from './HighEventVolume';

export const STATUS_COLORS = {
  OK: 'var(--colorUIGreen)',
  WARNING: 'var(--colorUIYellow)',
  ERROR: 'var(--colorUIRed)',
};

const STATUS_ICONS = {
  OK: CheckCircleIcon,
  WARNING: WarningIcon,
  ERROR: WarningIcon,
};

export type StatusType = 'OK' | 'WARNING' | 'ERROR';

const ROUTES_TO_HIDE = ['customer-display', 'express'];

interface Props {
  hideBorder?: boolean;
  blinking?: boolean;
}

/** Displays in a slide menu if there has been any system errors - Can for instance be if an order wasn't synced */
const SystemStatus: FC<Props> = ({ hideBorder, blinking }) => {
  const { i18n } = useI18n();

  const logger = useLogger('system-status');
  const location = useLocation();
  const showStatusModal = useModal();

  const errors = useSelector((state) => state.errors);

  const orderStatusEventsCount = useSelector((state) => state.orderStatusEvents.length);
  const tableEventsCount = useSelector((state) => state.tableEvents.length);

  const [ignoredHighEventWarnings, setIgnoredHighEventWarnings] = useState<Set<string>>(new Set());

  /** Some customers have reported slowdowns during super-high-pressure events, most likely caused by a lot of events locally being replayed */
  const hasOrderStatusWarning =
    orderStatusEventsCount > 4000 && !ignoredHighEventWarnings.has('orderStatusEvents');
  const hasTableEventsWarning =
    tableEventsCount > 7000 && !ignoredHighEventWarnings.has('tableEvents');

  const status: StatusType = errors.length
    ? 'ERROR'
    : hasOrderStatusWarning || hasTableEventsWarning
    ? 'WARNING'
    : 'OK';

  const handleIgnore = useCallback((type: 'orderStatusEvents' | 'tableEvents') => {
    setIgnoredHighEventWarnings((prev) => new Set(prev).add(type));
    logger.info('User ignored high event volume warning', { type });
  }, []);

  const icon = useMemo(
    () => (
      <Icon
        src={status === 'WARNING' ? WarningIcon : SystemIcon}
        color={status === 'OK' ? 'var(--colorBlueGray600)' : STATUS_COLORS[status]}
      />
    ),
    [status],
  );

  const background = useMemo(
    () =>
      blinking && status === 'WARNING'
        ? {
            color: 'var(--colorUIOrange)',
            blinking: true,
          }
        : undefined,
    [blinking, status],
  );

  const styles = useMemo(
    () => ({
      ...(hideBorder ? { borderTop: 'none' } : {}),
    }),
    [hideBorder],
  );

  if (ROUTES_TO_HIDE.includes(location.pathname)) {
    return null;
  }

  const statusHeading =
    status === 'OK'
      ? i18n('SystemStatus.Operational')
      : status === 'WARNING'
      ? i18n('SystemStatus.Warning')
      : i18n('SystemStatus.ExperiencingIssues');

  const buttonText =
    status === 'OK'
      ? i18n('SystemStatus.NoErrors')
      : status === 'WARNING'
      ? i18n('SystemStatus.Warning.CTA')
      : `${errors.length} ${i18n('SystemStatus.Errors').toLowerCase()}`;

  return (
    <>
      <IconButton
        background={background}
        icon={icon}
        text={buttonText}
        onClick={showStatusModal.toggle}
        styles={styles}
      />

      <SlideInPanel
        isShowing={showStatusModal.isShowing}
        hide={showStatusModal.hide}
        display="flex"
        flexDirection="column"
        gap="small">
        <InternetStatus />

        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          gap="small"
          textAlign="center">
          <Icon
            src={STATUS_ICONS[status]}
            color={STATUS_COLORS[status]}
            height="80px"
            width="80px"
          />
          <Box display="flex" flexDirection="column" gap="xs">
            <Heading level={1}>{statusHeading}</Heading>
            {status === 'OK' && (
              <Paragraph level={3}>{i18n('SystemStatus.Operational.Desc')}</Paragraph>
            )}
          </Box>
        </Box>

        {orderStatusEventsCount > 4000 && (
          <HighEventVolume
            type="orderStatusEvents"
            eventsQuantity={orderStatusEventsCount}
            onIgnore={handleIgnore}
          />
        )}

        {tableEventsCount > 8000 && (
          <HighEventVolume
            type="tableEvents"
            eventsQuantity={tableEventsCount}
            onIgnore={handleIgnore}
          />
        )}

        {errors.length > 0 && (
          <Box display="flex" flexDirection="column" gap="xs">
            <Paragraph level={4}>{_.capitalize(i18n('SystemStatus.Errors'))}:</Paragraph>
            {errors.map((e) => (
              <ErrorRow key={e.id} error={e} />
            ))}
          </Box>
        )}
      </SlideInPanel>
    </>
  );
};

export default memo(SystemStatus);
